import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Search, X, MapPin } from 'lucide-react';

export default function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  showOther = false,
  otherPlaceholder = 'Enter custom value',
  className = '',
  label = '',
  required = false,
  dropup = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setShowCustomInput(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    if (option === 'Other') {
      onChange('Other');
      setIsOpen(false);
      setSearchTerm('');
      setShowCustomInput(false);
    } else {
      onChange(option);
      setIsOpen(false);
      setSearchTerm('');
      setShowCustomInput(false);
    }
  };

  const handleCustomSubmit = () => {
    if (customValue.trim()) {
      onChange(customValue.trim());
      setIsOpen(false);
      setSearchTerm('');
      setShowCustomInput(false);
      setCustomValue('');
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
    setShowCustomInput(false);
    setCustomValue('');
  };

  const displayValue = value || placeholder;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-4 py-3 rounded-lg border text-left transition-all duration-200 ${
          disabled
            ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
            : isOpen
            ? 'border-emerald-500 bg-white ring-2 ring-emerald-100 shadow-sm'
            : 'border-gray-300 bg-white hover:border-emerald-300 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100'
        }`}
      >
        <div className="flex items-center justify-between">
          <span className={`truncate ${value ? 'text-gray-900' : 'text-gray-500'}`}>
            {value ? (
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-600" />
                {value}
              </span>
            ) : (
              placeholder
            )}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            {value && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Clear selection"
              >
                <X size={14} className="text-gray-400 hover:text-gray-600" />
              </button>
            )}
            <ChevronDown
              size={16}
              className={`text-gray-400 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </div>
        </div>
      </button>

      {/* Dropdown/Dropup */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: dropup ? 10 : -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: dropup ? 10 : -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 w-full ${dropup ? 'bottom-full mb-2' : 'top-full mt-2'} bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden`}
          >
            {showCustomInput ? (
              <div className="p-4 bg-gradient-to-br from-emerald-50 to-white">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                      Custom {label || 'Value'}
                    </label>
                    <input
                      type="text"
                      placeholder={otherPlaceholder}
                      value={customValue}
                      onChange={(e) => setCustomValue(e.target.value)}
                      className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleCustomSubmit();
                        } else if (e.key === 'Escape') {
                          setShowCustomInput(false);
                          setCustomValue('');
                        }
                      }}
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCustomSubmit}
                      disabled={!customValue.trim()}
                      className="flex-1 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setShowCustomInput(false);
                        setCustomValue('');
                      }}
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Search Input */}
                <div className="p-3 border-b border-gray-100 bg-gray-50">
                  <div className="relative">
                    <Search
                      size={16}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={`Search ${label.toLowerCase() || 'options'}...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all bg-white"
                    />
                  </div>
                </div>

                {/* Options List */}
                <div className="max-h-64 overflow-y-auto">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className={`w-full px-4 py-3 text-left text-sm transition-colors border-b border-gray-50 last:border-b-0 ${
                          value === option
                            ? 'bg-emerald-50 text-emerald-700 font-medium'
                            : 'text-gray-900 hover:bg-emerald-50 hover:text-emerald-700'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-emerald-600 opacity-70" />
                          {option}
                        </div>
                      </button>
                    ))
                  ) : searchTerm ? (
                    <div className="px-4 py-8 text-center">
                      <p className="text-sm text-gray-500 mb-2">
                        No {label.toLowerCase() || 'options'} found for "{searchTerm}"
                      </p>
                      {showOther && (
                        <button
                          type="button"
                          onClick={() => handleSelect('Other')}
                          className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                        >
                          Add "{searchTerm}" as custom option
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                      No {label.toLowerCase() || 'options'} available
                    </div>
                  )}

                  {/* Other Option */}
                  {showOther && !searchTerm && filteredOptions.length > 0 && (
                    <button
                      type="button"
                      onClick={() => handleSelect('Other')}
                      className="w-full px-4 py-3 text-left text-sm text-emerald-600 hover:bg-emerald-50 border-t border-gray-100 font-medium transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">+</span>
                        <span>Other (Enter custom {label.toLowerCase() || 'value'})</span>
                      </div>
                    </button>
                  )}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
